import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PolicyEndorsementsService {
  private http = inject(HttpClient);

  async getPolicyChange(guid: string): Promise<any> {
    try {
      return await firstValueFrom(this.http.get<any>(`policyChange/${guid}`));
    } catch (error) {
      throw new Error('Failed to fetch policy change data');
    }
  }

  /**
   * Submit a policy change request
   * @param model PolicyChangeViewModelREQ containing the policy change data
   * @returns Guid of the submitted policy change
   * @throws Error if unauthorized or request fails
   */
  async submitPolicyChangeRequest(model: FormData): Promise<string> {
    try {
      return await firstValueFrom(this.http.post<string>('policyChange/submit', model));
    } catch (error) {
      throw error;
    }
  }

  async upsertPolicyChangeRequest(model: FormData): Promise<string> {
    try {
      return await firstValueFrom(this.http.post<string>('policyChange', model));
    } catch (error) {
      throw error;
    }
  }

  async declinePolicyChangeRequest(model: any): Promise<string> {
    try {
      return await firstValueFrom(this.http.put<string>(`policyChange/decline/${model.policyChangeGuid}`, null));
    } catch (error) {
      throw new Error('Failed to decline policy change request');
    }
  }

  async issuePolicyChangeRequest(model: FormData): Promise<any> {
    try {
      return await firstValueFrom(this.http.post<any>('policyChange/issue', model));
    } catch (error: any) {
      let errorMessages: string[] = [];
      if (error?.error && typeof error.error === 'object') {
        for (const key of Object.keys(error.error)) {
          const value = error.error[key];
          if (Array.isArray(value) && value.every(msg => typeof msg === 'string')) {
            // Prefix with the key for clarity, e.g., "EffectiveDate: ..."
            errorMessages.push(...value.map(msg => `${msg}`));
          }
        }
      }
      throw new Error(errorMessages.length > 0 ? errorMessages.join(', ') : 'Failed to issue policy change request');
    }
  }

  async downloadApplication(policyChangeGuid: string): Promise<boolean> {
    try {
      const url = `PolicyChange/application-pdf/${policyChangeGuid}`;
      const response = (await firstValueFrom(
        this.http.get(url, { responseType: 'blob' as 'json', observe: 'response' }),
      )) as HttpResponse<Blob>;

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const fileName =
        response.headers.get('content-disposition')?.split('filename=')[1].split(';')[0].replace('"', '') ??
        'application.pdf';
      const contentType = response.body.type;
      const blob = new Blob([response.body], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(blobUrl);

      return true;
    } catch (error) {
      throw new Error('Failed to download application');
    }
  }

  async downloadPolicyChange(policyChangeGuid: string): Promise<boolean> {
    try {
      const url = `PolicyChange/download/${policyChangeGuid}`;
      const response = (await firstValueFrom(
        this.http.get(url, { responseType: 'blob' as 'json', observe: 'response' }),
      )) as HttpResponse<Blob>;

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const fileName =
        response.headers.get('content-disposition')?.split('filename=')[1].split(';')[0].replace('"', '') ??
        'policy-change.pdf';
      const contentType = response.body.type;
      const blob = new Blob([response.body], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(blobUrl);

      return true;
    } catch (error) {
      throw new Error('Failed to download policy change');
    }
  }

  async voidPolicyChangeRequest(model: FormData): Promise<string> {
    try {
      return await firstValueFrom(this.http.post<string>('policyChange/void', model));
    } catch (error: any) {
      let errorMessages: string[] = [];
      if (error?.error && typeof error.error === 'object') {
        for (const key of Object.keys(error.error)) {
          const value = error.error[key];
          if (Array.isArray(value) && value.every(msg => typeof msg === 'string')) {
            // Prefix with the key for clarity, e.g., "EffectiveDate: ..."
            errorMessages.push(...value.map(msg => `${msg}`));
          }
        }
      }
      throw new Error(errorMessages.length > 0 ? errorMessages.join(', ') : 'Failed to issue policy change request');
    }
  }
}
